/**
 * /subscribe/new — Public, unauthenticated self-serve checkout ("Door 1").
 *
 * Linked from the KCIS marketing site (Wix). No login, no session, no
 * gate. The visitor picks a tier, provides identity + contact info, and
 * submits — the backend creates a Square Subscription and emails them a
 * hosted invoice link. After payment confirms, the webhook auto-provisions
 * their KCIS account and mails a password-set link (same accept-invite
 * flow as Door 2 invites).
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, Mail, Building2, Phone, User, Sparkles,
  ArrowRight, ShieldCheck, ExternalLink, ArrowLeft,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  {
    key: "lite", name: "KCIS Lite", monthly_amount_usd: 49,
    features: [
      "Up to 25 active clients",
      "Standard reporting",
      "Standard practitioner dashboard",
      "Standard client management",
      "Email support",
    ],
    subtitle: "For solo practitioners getting started",
  },
  {
    key: "pro", name: "KCIS Pro", monthly_amount_usd: 149,
    features: [
      "Unlimited active clients",
      "Everything included in KCIS Lite",
      "Pro Assessment",
      "Pattern Engine",
      "Protocol templates",
      "Priority support",
    ],
    subtitle: "For growing practices",
    highlight: true,
  },
];

export default function SubscribeNew() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState("choose"); // choose | form | success
  const [selectedTier, setSelectedTier] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", clinic_name: "", phone: "",
  });
  const [success, setSuccess] = useState(null);
  const [existingRedirect, setExistingRedirect] = useState(null);

  // Honour ?plan=lite or ?plan=pro from marketing links. Preselect the
  // tile + jump straight to the form. The URL never grants access — the
  // backend + verified Square webhook determine the final tier.
  useEffect(() => {
    const p = (params.get("plan") || "").trim().toLowerCase();
    if (p === "lite" || p === "pro") {
      setSelectedTier(p);
      setStep("form");
    }
  }, [params]);

  const chooseTier = (key) => {
    setSelectedTier(key);
    setStep("form");
    setExistingRedirect(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error("Please provide your name and email.");
      return;
    }
    setSubmitting(true);
    setExistingRedirect(null);
    try {
      const r = await axios.post(`${API}/billing/public-checkout`, {
        tier: selectedTier,
        email: form.email.trim(),
        full_name: form.full_name.trim(),
        clinic_name: form.clinic_name.trim() || null,
        phone: form.phone.trim() || null,
      });
      const data = r.data || {};
      setSuccess(data);
      // Open Square-hosted checkout in a new tab and route this tab to
      // the activation page (which polls the backend for verified
      // payment status). If the browser blocks the popup, the activation
      // page shows an "Open Secure Square Checkout" button as a fallback.
      const opaqueRef = data.signup_ref || "";
      if (data.invoice_url) {
        try { window.open(data.invoice_url, "_blank", "noopener"); } catch (_e) {}
      }
      navigate(`/activate-account?ref=${encodeURIComponent(opaqueRef)}`);
      return;
    } catch (err) {
      const detail = err.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      const msg = typeof detail === "string" ? detail
        : detail?.message || "Could not start checkout. Please try again.";

      if (code === "already_subscribed" || code === "account_exists") {
        setExistingRedirect({ code, message: msg, redirect: detail?.redirect || "/admin/login" });
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="subscribe-new-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-purple-900 hover:opacity-80"
            data-testid="subscribe-new-logo-home"
            aria-label="Back to KCIS home"
          >
            <Sparkles className="w-5 h-5" />
            <span className="font-display tracking-wide text-lg">KCIS</span>
          </Link>
          <a href="/admin/login" className="text-xs text-slate-500 hover:text-purple-900" data-testid="subscribe-new-signin-link">
            Already have an account? Sign in →
          </a>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 pt-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-purple-900"
          data-testid="subscribe-new-back-home"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to KCIS Home
        </Link>
      </div>

      <main className="max-w-5xl mx-auto px-6 py-12">
        {step === "choose" && (
          <ChooseStep onChoose={chooseTier} />
        )}
        {step === "form" && (
          <FormStep
            tier={PLANS.find((p) => p.key === selectedTier)}
            form={form} setForm={setForm}
            submit={submit} submitting={submitting}
            existingRedirect={existingRedirect}
            onBack={() => { setStep("choose"); setExistingRedirect(null); }}
            navigate={navigate}
          />
        )}
        {step === "success" && (
          <SuccessStep success={success} navigate={navigate} />
        )}
      </main>

      <footer className="max-w-5xl mx-auto px-6 py-8 text-xs text-slate-400 border-t border-slate-200 mt-16 flex items-center justify-between flex-wrap gap-2">
        <span>KISSED Clinical Intelligence System · Educational wellness support. Not a substitute for licensed medical care.</span>
        <span className="flex items-center gap-3 flex-wrap">
          <a
            href="mailto:c.von@kissedbycvon.com?subject=KCIS%20Subscription%20Support"
            className="hover:text-purple-900 underline decoration-slate-300 hover:decoration-purple-900"
            data-testid="subscribe-new-footer-support"
          >
            Support
          </a>
          <span>Payments processed securely by Square.</span>
        </span>
      </footer>
    </div>
  );
}

function ChooseStep({ onChoose }) {
  return (
    <>
      <div className="max-w-2xl">
        <div className="text-xs uppercase tracking-widest text-purple-900 mb-3">Practitioner Subscription</div>
        <h1 className="font-display text-4xl sm:text-5xl text-slate-900 leading-tight">
          Start using KCIS today.
        </h1>
        <p className="mt-4 text-slate-600 leading-relaxed">
          Choose a plan below. Subscribe securely through Square. Once payment confirms,
          you'll get an email to set up your login — usually within a few minutes.
        </p>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10" data-testid="subscribe-new-plans">
        {PLANS.map((p) => (
          <div
            key={p.key}
            className={`relative rounded-2xl border p-7 bg-white transition hover:border-purple-400 hover:shadow-sm ${p.highlight ? "border-purple-900 shadow-md" : "border-slate-200"}`}
            data-testid={`subscribe-new-plan-${p.key}`}
          >
            {p.highlight && (
              <div className="absolute -top-3 left-6 bg-purple-900 text-white text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full">
                Most Popular
              </div>
            )}
            <div className="font-display text-2xl text-slate-900">{p.name}</div>
            <div className="text-xs text-slate-500 mt-1">{p.subtitle}</div>
            <div className="mt-5 text-4xl font-display text-slate-900">
              ${p.monthly_amount_usd}<span className="text-sm text-slate-500 font-sans">/month</span>
            </div>
            <div
              className="mt-1 text-[11px] text-slate-500 leading-relaxed"
              data-testid={`subscribe-new-recurring-disclosure-${p.key}`}
            >
              Auto-renews monthly at ${p.monthly_amount_usd}. Cancel anytime from your account. Applicable taxes may apply. Processed by Square.
            </div>
            <ul className="mt-6 space-y-2 text-sm text-slate-700">
              {p.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />{f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => onChoose(p.key)}
              className={`mt-7 w-full h-12 rounded-xl inline-flex items-center justify-center gap-2 text-sm font-medium transition ${p.highlight ? "bg-purple-900 text-white hover:bg-purple-800" : "bg-slate-900 text-white hover:bg-slate-800"}`}
              data-testid={`subscribe-new-choose-${p.key}`}
            >
              Choose {p.name}<ArrowRight className="w-4 h-4" />
            </button>
          </div>
        ))}
      </section>
    </>
  );
}

function FormStep({ tier, form, setForm, submit, submitting, existingRedirect, onBack, navigate }) {
  return (
    <div className="max-w-xl">
      <button onClick={onBack} className="text-xs text-slate-500 hover:text-purple-900 mb-3" data-testid="subscribe-new-back">
        ← Back to plans
      </button>
      <div className="rounded-2xl border border-slate-200 bg-white p-7" data-testid="subscribe-new-form-card">
        <div className="text-xs uppercase tracking-widest text-purple-900 mb-2">Step 2 of 2</div>
        <h2 className="font-display text-2xl text-slate-900">
          Your details for <span className="text-purple-900">{tier?.name}</span> — ${tier?.monthly_amount_usd}/month
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          We'll email you a secure Square payment link. You'll set your KCIS password after payment confirms.
        </p>

        {existingRedirect && (
          <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="subscribe-new-existing-notice">
            <div className="text-sm text-amber-900 font-medium">{existingRedirect.message}</div>
            <button
              onClick={() => navigate(existingRedirect.redirect || "/admin/login")}
              className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-amber-700 hover:bg-amber-800 text-white h-9 px-4 text-sm font-medium"
              data-testid="subscribe-new-existing-login"
            >
              Log In<ExternalLink className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        <form onSubmit={submit} className="mt-5 space-y-4">
          <Field label="Full name *" icon={User}>
            <input
              required
              type="text"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:border-purple-900"
              placeholder="Dr. Jane Doe"
              data-testid="subscribe-new-input-name"
            />
          </Field>
          <Field label="Email *" icon={Mail}>
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:border-purple-900"
              placeholder="jane@yourclinic.com"
              data-testid="subscribe-new-input-email"
            />
          </Field>
          <Field label="Clinic or business name (optional)" icon={Building2}>
            <input
              type="text"
              value={form.clinic_name}
              onChange={(e) => setForm({ ...form, clinic_name: e.target.value })}
              className="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:border-purple-900"
              data-testid="subscribe-new-input-clinic"
            />
          </Field>
          <Field label="Phone (optional)" icon={Phone}>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:border-purple-900"
              data-testid="subscribe-new-input-phone"
            />
          </Field>

          <button
            type="submit"
            disabled={submitting}
            className="w-full h-12 rounded-xl bg-purple-900 hover:bg-purple-800 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 mt-2"
            data-testid="subscribe-new-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Continue to Square Checkout
          </button>
          <p
            className="text-[11px] text-slate-500 text-center mt-2 leading-relaxed"
            data-testid="subscribe-new-recurring-disclosure-form"
          >
            Auto-renews monthly at ${tier?.monthly_amount_usd}. Cancel anytime from your account. Applicable taxes may apply. Processed by Square.
            <br />
            By continuing you agree to KCIS's terms.
          </p>
        </form>
      </div>
    </div>
  );
}

function SuccessStep({ success, navigate }) {
  return (
    <div className="max-w-xl" data-testid="subscribe-new-success">
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8">
        <div className="w-12 h-12 rounded-full bg-emerald-600 text-white inline-flex items-center justify-center mb-4">
          <CheckCircle2 className="w-6 h-6" />
        </div>
        <h2 className="font-display text-2xl text-emerald-900">Almost there.</h2>
        <p className="mt-3 text-emerald-900 leading-relaxed">
          {success?.message ||
            "Check your email for a secure payment link from Square to complete your subscription."}
        </p>
        {success?.invoice_url && (
          <a
            href={success.invoice_url}
            target="_blank" rel="noopener noreferrer"
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white h-11 px-5 text-sm font-medium"
            data-testid="subscribe-new-open-invoice"
          >
            Open Square Invoice<ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
        <div className="mt-6 pt-5 border-t border-emerald-200 text-sm text-emerald-900">
          Once payment confirms, we'll email you a link to set your password and log in.
        </div>
      </div>
      <div className="mt-5 text-center">
        <button
          onClick={() => navigate("/admin/login")}
          className="text-xs text-slate-500 hover:text-purple-900"
          data-testid="subscribe-new-success-login-link"
        >
          Already set your password? Sign in →
        </button>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-600 mb-1.5 inline-flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5 text-slate-400" />
        {label}
      </span>
      {children}
    </label>
  );
}
